import React from "react";
import { useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";

import {
  T,
  formatClock,
  photoAngles,
  PHOTO_STAGES,
  PHOTO_STAGE_TITLE,
} from "../../constants";
import { useUi } from "../../ui";
import { vehiclePhotosForBooking } from "../queries";
import LoadingIndicator from "../../../../components/LoadingIndicator";

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  count: { fontSize: 11, color: T.mutedFg },
  hint: { margin: "4px 0 0", fontSize: 11, color: T.mutedFg },
  grid: {
    marginTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  /* Sits where the tiles will be, so the box does not jump once they land. */
  loading: { marginTop: 8, display: "flex", height: 84, alignItems: "center", justifyContent: "center" },
  tile: {
    display: "block",
    overflow: "hidden",
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: T.surface,
    textDecoration: "none",
  },
  image: {
    display: "block",
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    background: T.muted,
  },
  missing: {
    display: "flex",
    aspectRatio: "16 / 9",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color: T.subtleFg,
    background: T.muted,
  },
  foot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "6px 10px",
    fontSize: 12,
  },
  label: { fontWeight: 600, color: T.fg },
  meta: { color: T.mutedFg, fontSize: 11 },
});

/* Who shot the set and why anyone is looking at it. */
const STAGE_COPY = {
  [PHOTO_STAGES.INTAKE]: {
    none: "No photos were taken at intake for this car.",
    failed: "The intake photos could not be loaded.",
    taken: "Taken by the concierge at intake. Check the car against them.",
  },
  [PHOTO_STAGES.PARKING]: {
    none: "No photos were taken where this car was parked.",
    failed: "The parking photos could not be loaded.",
    taken: "Taken by the porter where the car was parked.",
  },
  [PHOTO_STAGES.HANDOVER]: {
    none: "No photos were taken on the way back to the guest.",
    failed: "The handover photos could not be loaded.",
    taken: "Taken by the porter on the way back to the guest.",
  },
};

/*
 * One stage's condition photos, read-only. Whoever is looking is checking the
 * car against them, not retaking them: every capture flow lives on the screen
 * that owns its stage, and these are already in S3 against the booking.
 */
export default function VehiclePhotoGallery({
  bookingId,
  facilityId,
  stage = PHOTO_STAGES.INTAKE,
}) {
  const s = useStyles();
  const ui = useUi();

  /* viewUrl is a short-lived presigned GET, so a cached one would be dead by
   * the time it renders. Always go to the network. */
  const { data, loading, error } = useQuery(vehiclePhotosForBooking, {
    variables: { bookingId, facilityId, stage },
    skip: !bookingId || !facilityId,
    fetchPolicy: "network-only",
  });

  const photos = data?.vehiclePhotos ?? [];
  const byAngle = new Map(photos.map((photo) => [photo.angle, photo]));

  const copy = STAGE_COPY[stage] ?? STAGE_COPY[PHOTO_STAGES.INTAKE];

  const note = !bookingId
    ? "No booking on file for this car, so there are no photos."
    : error
    ? copy.failed
    : photos.length === 0
    ? copy.none
    : copy.taken;

  return (
    <div>
      <div className={s.head}>
        <div className={ui.eyebrow}>{PHOTO_STAGE_TITLE[stage]}</div>
        <div className={s.count}>
          {photos.length}/{photoAngles.length}
        </div>
      </div>
      { !loading && <p className={s.hint}>{note}</p> }

      {loading && (
        <div className={s.loading} aria-busy="true">
          <LoadingIndicator />
        </div>
      )}

      {photos.length > 0 && (
        <div className={s.grid}>
          {photoAngles.map(({ angle, label }) => {
            const photo = byAngle.get(angle);

            /* New tab rather than a lightbox: the runner is on a tablet and the
             * browser's own viewer zooms better than anything here would. */
            return (
              <a
                key={angle}
                className={s.tile}
                href={photo?.viewUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  if (!photo) e.preventDefault();
                }}
              >
                {photo ? (
                  <img className={s.image} src={photo.viewUrl} alt={label} />
                ) : (
                  <div className={s.missing}>{label} · not captured</div>
                )}
                <div className={s.foot}>
                  <span className={s.label}>{label}</span>
                  {photo?.capturedAt && (
                    <span className={s.meta}>{formatClock(photo.capturedAt)}</span>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
